import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

export type CountryConfig = {
    code: string;
    name: { az: string; en: string; ru: string };
    phone_prefix: string;
    phone_pattern: string | null;
    id_field: { label_az?: string; label_en?: string; label_ru?: string; pattern?: string; required?: boolean; help_az?: string };
    currency: string;
    date_format: string;
    address_fields: Array<{ code: string; label_az: string; label_en: string; label_ru: string; required: boolean }>;
    timezone_default: string;
};

const cache: Record<string, CountryConfig | null> = {};

/** Fetches the country pack for the active tenant. Returns null while loading or when no country set. */
export function useTenantCountry(): CountryConfig | null {
    const { activeMembership } = useAuth();
    const code = (activeMembership?.tenant.country || "").toUpperCase();
    const [cfg, setCfg] = useState<CountryConfig | null>(code in cache ? cache[code] : null);
    useEffect(() => {
        if (!code) { setCfg(null); return; }
        if (code in cache) { setCfg(cache[code]); return; }
        let alive = true;
        api.get<CountryConfig>(`/countries/${code}`).then((r) => {
            cache[code] = r.data;
            if (alive) setCfg(r.data);
        }).catch(() => { cache[code] = null; if (alive) setCfg(null); });
        return () => { alive = false; };
    }, [code]);
    return cfg;
}

export function pickLocalized(cfg: { az?: string; en?: string; ru?: string } | undefined, lang: string, fallback: string): string {
    if (!cfg) return fallback;
    const key = (lang.startsWith("ru") ? "ru" : lang.startsWith("en") ? "en" : "az") as "az" | "en" | "ru";
    return cfg[key] || cfg.az || cfg.en || fallback;
}
